import React, { useRef, useEffect } from 'react';

interface HeatmapPoint {
  x: number;
  y: number;
  confidence: number; // 0-1
}

interface HeatmapOverlayProps {
  points: HeatmapPoint[];
  width: number;
  height: number;
  opacity?: number; // 0-1, default 0.6
}

/**
 * Overlay heatmap disegnata su un canvas trasparente.
 * Usata per mostrare la distribuzione spaziale degli eventi (es. attacchi).
 */
const HeatmapOverlay: React.FC<HeatmapOverlayProps> = ({
  points,
  width,
  height,
  opacity = 0.6,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Pulisci il canvas
    ctx.clearRect(0, 0, width, height);

    if (points.length === 0) return;

    // Crea un array di intensità per ogni punto
    // Useremo un accumulatore in un canvas off‑screen per il blending
    const offCanvas = document.createElement('canvas');
    offCanvas.width = width;
    offCanvas.height = height;
    const offCtx = offCanvas.getContext('2d');
    if (!offCtx) return;

    // Imposta il compositing per accumulare trasparenze
    offCtx.globalCompositeOperation = 'source-over';
    offCtx.fillStyle = 'rgba(59, 130, 246, 0.0)';
    offCtx.fillRect(0, 0, width, height);

    // Per ogni punto disegna un cerchio sfumato
    for (const point of points) {
      // Raggio variabile con la confidenza (max 40px, min 10px)
      const radius = 10 + point.confidence * 30;
      const alpha = 0.15 + point.confidence * 0.25; // trasparenza base

      // Gradiente radiale: più intenso al centro
      const gradient = offCtx.createRadialGradient(
        point.x, point.y, 0,
        point.x, point.y, radius,
      );
      gradient.addColorStop(0, `rgba(59, 130, 246, ${alpha})`);
      gradient.addColorStop(0.6, `rgba(59, 130, 246, ${alpha * 0.5})`);
      gradient.addColorStop(1, 'rgba(59, 130, 246, 0)');

      offCtx.beginPath();
      offCtx.arc(point.x, point.y, radius, 0, 2 * Math.PI);
      offCtx.fillStyle = gradient;
      offCtx.fill();
    }

    // Applica l'opacità complessiva
    ctx.globalAlpha = opacity;
    ctx.drawImage(offCanvas, 0, 0);
    ctx.globalAlpha = 1.0;
  }, [points, width, height, opacity]);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        pointerEvents: 'none', // non intercetta i click
      }}
    />
  );
};

export default HeatmapOverlay;